/**
 * Shared by the Release posts composer (dashboard /admin/social) and the API that drafts its posts.
 * The rules behind these live in docs/SOCIAL.md.
 */

/** Pages on metrickle.com a release post can send people to. Keep in step with apps/web/public. */
export const SOCIAL_LANDING_PAGES = [
  { path: "/", label: "Home" },
  { path: "/features/release-verdicts", label: "Release verdicts" },
  { path: "/features/protected-tasks", label: "Protected tasks" },
  { path: "/features/funnels", label: "Funnels" },
  { path: "/features/journeys", label: "Journeys" },
  { path: "/features/friction", label: "Friction" },
  { path: "/features/heatmaps-and-replay", label: "Heatmaps and replay" },
  { path: "/features/surveys", label: "Surveys" },
  { path: "/features/integrations", label: "Integrations" },
  { path: "/features/ai-assistants", label: "AI assistants" },
  { path: "/solutions/conversion", label: "Conversion" },
  { path: "/solutions/accessibility", label: "Accessibility" },
  { path: "/solutions/ux-research", label: "UX research" },
  { path: "/solutions/agencies", label: "Agencies" },
  { path: "/pricing", label: "Pricing" },
] as const;

export type SocialLandingPath = (typeof SOCIAL_LANDING_PAGES)[number]["path"];
export const SOCIAL_LANDING_PATHS = SOCIAL_LANDING_PAGES.map((p) => p.path) as [SocialLandingPath, ...SocialLandingPath[]];

/** docs/SOCIAL.md's content pillars, used as utm_campaign. A shipped feature is `build`. */
export const SOCIAL_PILLARS = ["build", "release", "a11y", "myth"] as const;
export type SocialPillar = (typeof SOCIAL_PILLARS)[number];

export type SocialNetwork = "x" | "linkedin";

export const X_LIMIT = 280;
export const LINKEDIN_LIMIT = 3000;
/** Roughly where LinkedIn cuts a post off with "…see more" on desktop. The hook has to land before it. */
export const LINKEDIN_FOLD = 210;

/** The tagged link for a post's first reply or comment (links in the post itself are shown to fewer people). */
export function socialLink(path: string, network: SocialNetwork, pillar: SocialPillar = "build"): string {
  const url = new URL(path, "https://metrickle.com");
  url.searchParams.set("utm_source", network);
  url.searchParams.set("utm_medium", "social");
  url.searchParams.set("utm_campaign", pillar);
  return url.toString();
}

const URL_RE = /https?:\/\/\S+/g;
const EMOJI_RE = /\p{Extended_Pictographic}(?:️|‍\p{Extended_Pictographic}|\p{Emoji_Modifier})*/gu;

/**
 * Length as X counts it, near enough: links are 23, emoji 2, Latin and common punctuation 1,
 * and other scripts (CJK and the like) 2.
 */
export function xLength(text: string): number {
  let n = 0;
  const rest = text
    .replace(URL_RE, () => {
      n += 23;
      return "";
    })
    .replace(EMOJI_RE, () => {
      n += 2;
      return "";
    });
  for (const ch of rest) {
    const c = ch.codePointAt(0)!;
    const light = c <= 0x10ff || (c >= 0x2000 && c <= 0x200d) || (c >= 0x2010 && c <= 0x201f) || (c >= 0x2032 && c <= 0x2037);
    n += light ? 1 : 2;
  }
  return n;
}

/** Words docs/SOCIAL.md keeps out of public posts, with what to say instead. */
export const SOCIAL_BANNED_WORDS: { word: RegExp; instead: string }[] = [
  { word: /\bcontracts?\b/i, instead: "protected task" },
  { word: /\bbaselines?\b/i, instead: "before the release" },
  { word: /\bp\d{2}\b/i, instead: "plain words, like “most people”" },
  { word: /\b(net|gross)\b/i, instead: "plain words, like “kept” or “all”" },
];

/** The banned words in a draft, so the composer can flag them before anything is posted. */
export function bannedWordsIn(text: string): { found: string; instead: string }[] {
  return SOCIAL_BANNED_WORDS.flatMap(({ word, instead }) => {
    const m = text.match(word);
    return m ? [{ found: m[0], instead }] : [];
  });
}
